/* global Word */

import { AnalyzeResult, ResolutionStatus, LawCase } from "../models/benchly.models";
import { wordApiColors } from "../theme/benchly-theme";

const CITATION_HYPERLINK_COLOR = wordApiColors.hyperlinkBlue;

/**
 * Decide which LawCase should drive the hyperlink for a given AnalyzeResult.
 *
 * Priority:
 *  1) resolvedLawCase if it is an object (selected/curated)
 *  2) first certified LawCase in citations
 *  3) otherwise null (skip)
 */
function pickLawCaseForHyperlink(result: AnalyzeResult): LawCase | null {
  const resolved = result.resolvedLawCase;

  if (resolved && resolved !== "none") {
    return resolved;
  }

  const certified = (result.citations ?? []).find(
    (c) => c.status === ResolutionStatus.ValidatedAndCertified
  );

  return certified ?? null;
}

/**
 * Applies hyperlinks to citation bookmarks for results that have a certified resolution.
 * Uses bookmark ranges instead of content controls for hyperlink application.
 *
 * Behavior for ParallelCites:
 * - Applies ONE hyperlink to the full bookmarked span.
 * - Chooses URL from resolvedLawCase if present, else first certified citation.
 */
export async function applyHyperlinksToCertifiedCitations(
  analysisResults: AnalyzeResult[]
): Promise<number> {
  // Only keep results we can resolve to a certified LawCase with a URL
  const applicable = analysisResults
    .map((r) => {
      const lawCase = pickLawCaseForHyperlink(r);
      const url = lawCase?.foundDocs?.[0]?.hyperlink;
      const isCertified = lawCase?.status === ResolutionStatus.ValidatedAndCertified;
      return { result: r, lawCase, url, isCertified };
    })
    .filter((x) => Boolean(x.result.documentReferenceId) && Boolean(x.url) && x.isCertified);

  if (applicable.length === 0) return 0;

  return await Word.run(async (context) => {
    let appliedCount = 0;

    const pendingUpdates: Array<{
      hyperlinkUrl: string;
      bookmarkRange: Word.Range;
      bookmarkName: string;
    }> = [];

    // Queue loads (no sync in loop)
    for (const item of applicable) {
      const bookmarkName = item.result.documentReferenceId!;
      const hyperlinkUrl = item.url!;

      const bookmarkRange = context.document.getBookmarkRangeOrNullObject(bookmarkName);
      bookmarkRange.load("isNullObject,text");
      pendingUpdates.push({ hyperlinkUrl, bookmarkRange, bookmarkName });
    }

    // Resolve all ranges (single roundtrip)
    await context.sync();

    // Apply changes (queued) then one final sync
    for (const { hyperlinkUrl, bookmarkRange, bookmarkName } of pendingUpdates) {
      try {
        if (bookmarkRange.isNullObject) {
          console.warn(`[CitationHyperlink] Bookmark "${bookmarkName}" not found, skipping.`);
          continue;
        }

        const visibleText = (bookmarkRange.text ?? "").trim();
        if (!visibleText) continue;

        (bookmarkRange as any).hyperlink = hyperlinkUrl;

        bookmarkRange.font.color = CITATION_HYPERLINK_COLOR;
        bookmarkRange.font.underline = Word.UnderlineType.single;

        appliedCount += 1;
      } catch (error) {
        console.error(
          `[CitationHyperlink] Failed to apply hyperlink for bookmark="${bookmarkName}".`,
          error
        );
      }
    }

    await context.sync();
    return appliedCount;
  });
}
